import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import IDPGuard from './guard-client';
import { setAuthenticated, setLoading, setToken } from './auth-slice';
import { AuthContext } from './auth-context';
import type { AuthProviderProps } from './auth-context.types';
import { Alert, Box, Typography } from '@mui/material';
import { ArtButton } from '@art/shared-ui';

export default function GuardProvider({
  children,
  idp_uri,
  redirect_uri,
  get_user,
  onLoginComplete,
}: AuthProviderProps) {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const authenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [authGuardInstance, setAuthGuardInstance] = useState<IDPGuard | null>(null);
  const [initializationFailed, setInitializationFailed] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    const initializeAuth = async () => {
      const idpGuard = new IDPGuard({ idp_uri, redirect_uri, get_user }, dispatch);
      setInitializationFailed(false);
      dispatch(setLoading(true));
      try {
        const auth = await idpGuard.init();
        if (!active) return;

        const tokenResponse = {
          tenantName: idpGuard.tenantName,
          token: idpGuard.token,
          clientID: idpGuard.clientId,
        };
        dispatch(setToken(tokenResponse));
        dispatch(setAuthenticated(auth));

        setAuthGuardInstance(idpGuard);
        onLoginComplete(tokenResponse);
      } catch (error) {
        if (!active) return;
        console.error('[auth] Initialization failed', error);
        dispatch(
          setToken({ tenantName: null, token: null, clientID: null }),
        );
        dispatch(setAuthenticated(false));
        setAuthGuardInstance(idpGuard);
        setInitializationFailed(true);
      } finally {
        if (active) dispatch(setLoading(false));
      }
    };

    void initializeAuth();
    return () => {
      active = false;
    };
  }, [dispatch, idp_uri, redirect_uri, retryAttempt]);

  const value = useMemo(() => ({ authenticated, token: token || '', authGuardInstance }),
    [authenticated, token, authGuardInstance]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {initializationFailed ? (
        <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3 }}>
          <Box sx={{ width: '100%', maxWidth: 520 }}>
            <Typography variant="h5" component="h1" sx={{ mb: 2 }}>Unable to verify your session</Typography>
            <Alert severity="error" sx={{ mb: 2 }}>Your session could not be verified. Please try again.</Alert>
            <ArtButton label="Retry" fullWidth={false} onClick={() => setRetryAttempt(attempt => attempt + 1)} />
          </Box>
        </Box>
      ) : children}
    </AuthContext.Provider>
  );
}
